import { CbhIcon, Pill, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { IconButton } from "@src/appV2/redesign/components/IconButton";

import { type PlacementData } from "../../../types/placement";
import { PlacementRecentlyAddedPill } from "./PlacementRecentlyAddedPill";
import { PlacementTagsPill } from "./PlacementTagsPill";

interface PlacementCardHeaderProps {
  isFavorite?: boolean;
  blurPlacement: boolean;
  tags: PlacementData["tags"];
  workerRequirements: PlacementData["workerRequirements"];
  ongoingFavoritingRequest: boolean;
  placementHeaderStatus?: React.ReactNode;
  isRecentlyAdded: boolean;

  onFavoriteToggle?: (isFavorite: boolean) => void;
}

export function PlacementCardHeader(props: PlacementCardHeaderProps) {
  const {
    isFavorite,
    blurPlacement,
    tags,
    workerRequirements,
    ongoingFavoritingRequest,
    onFavoriteToggle,
    placementHeaderStatus,
    isRecentlyAdded,
  } = props;

  function getMissingDocumentsCount(
    workerRequirements: PlacementData["workerRequirements"],
    legacyDocumentRequirementId?: string
  ): number {
    if (!workerRequirements) {
      return 0;
    }

    return workerRequirements.filter((requirement) => {
      // Exclude the legacy document requirement
      if (
        legacyDocumentRequirementId &&
        requirement.requirement.id === legacyDocumentRequirementId
      ) {
        return false;
      }

      return !requirement.isValid && requirement.requirement.visibleToHCP;
    }).length;
  }

  const missingDocumentsCount = getMissingDocumentsCount(workerRequirements);

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
      <Stack direction="row" spacing={1} alignItems="center">
        <PlacementTagsPill tags={tags} />
        {isRecentlyAdded && <PlacementRecentlyAddedPill blurred={blurPlacement} />}
        {placementHeaderStatus}
        {missingDocumentsCount > 0 && (
          <Pill
            variant="outlined"
            size="small"
            label={
              <Stack direction="row" alignItems="center" gap={1}>
                <CbhIcon type="document" size="xSmall" />
                <Text variant="body2">{missingDocumentsCount}</Text>
              </Stack>
            }
            backgroundColor={(theme) => theme.palette.background.tertiary}
            color={(theme) => theme.palette.text.secondary}
            sx={{ width: "fit-content", alignSelf: "flex-start" }}
          />
        )}
      </Stack>

      <IconButton
        iconType={isFavorite ? "like-filled" : "like-outlined"}
        variant="ghost"
        disabled={ongoingFavoritingRequest}
        sx={{
          height: 28,
          width: 28,
          padding: 2.5, // Added additional padding to increase tap target size
          margin: -2.5,
        }}
        onClick={(event) => {
          if (isDefined(onFavoriteToggle)) {
            event.stopPropagation();
            event.preventDefault();
            onFavoriteToggle(!isFavorite);
          }
        }}
      />
    </Stack>
  );
}
